/* eslint-disable prettier/prettier */

import { DateTime } from 'luxon';
import {
  all,
  call,
  debounce,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';

import { DEFAULT_SEARCH_VALUE_TIMEZONE, TRIP_STATUS_CODES } from '@/constants';
import {
  configApi,
  contractApi,
  googleMapsApi,
  kinetikProxyApi,
  lyftApi,
  memberApi,
  notificationApi,
  reconciliationApi,
  tripApi,
  userApi,
} from 'api';
import { BOOKING_TYPE_NAMES } from 'components/TripTable/constants';
import { BOOKING_TYPE_KEYS, TNC_PROVIDERS } from 'utils/constants';
import customDebounce from 'utils/customDebounce';
import { isAuthorizationError } from 'utils/errors';
import { formatGeocode, getAddressComponentFromGeoCode } from 'utils/helpers';
import {
  disconnectUserFromSendbird,
  getTotalUnreadMessageCount,
} from 'utils/sendbird';
import { getTargetTripGrouping, getTripQueryFromTripGrouping } from 'utils/v2';

import {
  authError,
  createAccessTokenError,
  createAccessTokenSuccess,
  fetchAppointmentReasonsError,
  fetchAppointmentReasonsSuccess,
  fetchAssigneesError,
  fetchAssigneesSuccess,
  fetchContractsError,
  fetchContractsSuccess,
  fetchEstimateError,
  fetchEstimateSuccess,
  fetchLocationsError,
  fetchLocationsSuccess,
  fetchLyftDriverAvailabilityError,
  fetchLyftDriverAvailabilitySuccess,
  getLevelsOfServiceSuccess,
  getLocationModifiersSuccess,
  getMemberEnrollmentCoverageTripCountsError,
  getMemberEnrollmentCoverageTripCountsSuccess,
  getMemberPreferencesFailed,
  getMemberPreferencesSuccess,
  getProCredExConnectionStatusError,
  getProCredExConnectionStatusSuccess,
  getTripApprovalReasonsError,
  getTripApprovalReasonsSuccess,
  getTripAssignmentPreferenceError,
  getTripAssignmentPreferenceSuccess,
  getTripCancellationReasonsError,
  getTripCancellationReasonsSuccess,
  getTripDenialReasonsError,
  getTripDenialReasonsSuccess,
  getTripEscalationReasonsError,
  getTripEscalationReasonsSuccess,
  getTripGroupingsError,
  getTripGroupingsSuccess,
  getTripPreferencesError,
  getTripPreferencesSuccess,
  getUnreadNotificationsError,
  getUnreadNotificationsSuccess,
  getVehicleCategoriesError,
  getVehicleCategoriesSuccess,
  globalSearchError,
  globalSearchSuccess,
  loadOrganizationsError,
  loadOrganizationsSuccess,
  logoutSuccess,
  rolesLoaded,
  searchMembersError,
  searchMembersSuccess,
  selectLocationSuccess,
  setKinetikProxyToken,
  setLyftToken,
  setSidebarMenuCounts,
  setTncContracts,
  setUnreadMessageCount,
  throwAlertMsg,
  throwError,
  throwErrorToastNotification,
  throwMiscError,
  throwSuccessMsg,
  updateTripPreferencesFailed,
  updateTripPreferencesSuccess,
  userLoaded,
} from './actions';
import {
  allowedIndustrySegmentCodes,
  ATTEMPT_GET_MEMBER_PREFERENCES,
  ATTEMPT_UPDATE_MEMBER,
  ATTEMPT_UPDATE_MEMBER_PREFERENCES,
  ATTEMPT_UPDATE_TRIP_PREFERENCES,
  CREATE_ACCESS_TOKEN,
  FETCH_LYFT_DRIVER_AVAILABILITY,
  GET_LYFT_TRIP_ESTIMATION,
  GET_MEMBER_ENROLLMENT_COVERAGE_TRIP_COUNTS,
  GET_NEEDS_ACTION_TRIPS_COUNT,
  GET_PROCREDEX_CONNECTION_STATUS,
  GET_TRIP_APPROVAL_REASON,
  GET_TRIP_ASSIGNMENT_PREFERENCE,
  GET_TRIP_CANCELLATION_REASONS,
  GET_TRIP_DENIAL_REASONS,
  GET_TRIP_ESCALATION_REASON,
  GET_TRIP_GROUPINGS,
  GET_TRIP_PREFERENCES,
  GET_TRIPS_COUNT,
  GET_UBER_TRIP_ESTIMATION,
  GET_UNREAD_MESSAGE_COUNT,
  GET_UNREAD_NOTIFICATIONS,
  GET_VEHICLE_CATEGORIES,
  GLOBAL_SEARCH,
  GLOBAL_SEARCH_RESULTS_LIMIT_PER_RESOURCE,
  ON_FETCH_APPOINTMENT_REASONS,
  ON_FETCH_ASSIGNEES,
  ON_FETCH_CONTRACTS,
  ON_FETCH_LOCATIONS,
  ON_GET_LEVELS_OF_SERVICE,
  ON_GET_LOCATION_MODIFIERS,
  ON_LOAD_ORGANIZATIONS,
  ON_LOAD_ROLES,
  ON_LOAD_USER,
  ON_LOGOUT,
  ON_SELECT_LOCATION,
  SEARCH_MEMBERS,
  SORT_PARAMS,
} from './constants';
import {
  makeSelectActiveOrgResourceId,
  makeSelectAppState,
  makeSelectOrgCoordinates,
} from './selectors';

function* searchMembers({ params, orgResourceId, callback = null }) {
  /**
   * The callback function sets the search input value into the respective container's redux state.
   * The value set on the redux level is used to conditionally toggle {freeSolo} prop at SearchBox component.
   * NEED TO REFACTOR THE CALLBACK
   */
  if (callback !== null) {
    callback(params);
  }
  const { activeOrganization } = yield select(makeSelectAppState());
  try {
    const options = {
      params: {
        ...params,
        searchValueTimezone:
          activeOrganization?.timezone || DEFAULT_SEARCH_VALUE_TIMEZONE,
      },
    };
    const { patients: members, totalReturned } = yield call(
      memberApi.searchMembers,
      orgResourceId,
      options,
    );
    yield put(searchMembersSuccess(members, totalReturned));
  } catch (error) {
    yield all([
      put(
        throwMiscError('There was an error finding members. Please try again.'),
      ),
      put(searchMembersError()),
    ]);
  }
}

/**
 * Fetches logged in user
 *
 */
function* loadingUser() {
  try {
    const { user } = yield call(userApi.self);
    const idPortalChangePasswordUrl = `${process.env.KINETIK_ID_PORTAL_URL}account-management/change-password`;

    if (user.passwordIsExpired)
      // IF we figure out a better UX for force booting a user from this UI back to ID Portal because of an expired password,
      // here is where we can do it.
      yield put(
        throwAlertMsg(
          'Your password has expired. You will be required to provide a new one on your next login, or you can update now on the ID Portal.',
          idPortalChangePasswordUrl,
          'ID Portal',
        ),
      );
    else {
      const passwordExpiresOn = DateTime.fromISO(
        user.passwordExpiresOn,
      ).diffNow(['days', 'hours']);

      if (passwordExpiresOn.get('days') < 7)
        yield put(
          throwAlertMsg(
            `Your password is set to expire in ${passwordExpiresOn.get(
              'days',
            )} day(s) and ${Math.floor(
              passwordExpiresOn.get('hours'),
            )} hour(s). Please update your account with a new password on the ID Portal.`,
            idPortalChangePasswordUrl,
            'ID Portal',
          ),
        );
    }
    yield put(userLoaded(user));
  } catch (error) {
    yield put(
      authError(error.response ? error.response.data.message : error.message),
    );
  }
}

/**
 * Logout user
 *
 */
function* logout() {
  try {
    yield all([call(userApi.logout), call(disconnectUserFromSendbird)]);
    yield put(logoutSuccess());
  } catch (error) {
    yield put(
      authError(error.response ? error.response.data.message : error.message),
    );
  }
}

function* fetchLocations({ key, searchTerm, itemId }) {
  const coordinates = yield select(makeSelectOrgCoordinates());

  try {
    const { predictions } = yield call(googleMapsApi.getAutoComplete, {
      params: {
        input: searchTerm,
        components: 'country:us',
        // types: ['address'],
        ...(coordinates && {
          location: `${coordinates[1]},${coordinates[0]}`,
          radius: 50000,
        }),
      },
    });
    const places = predictions.map((p) => ({
      formattedAddress: p.description,
      placeID: p.place_id,
    }));
    yield put(fetchLocationsSuccess(`${key}Locations`, places, itemId));
  } catch (error) {
    yield all([
      put(throwError(error)),
      put(fetchLocationsError(`${key}Locations`, itemId)),
    ]);
  }
}

function* fetchGeocode({
  key,
  location,
  action,
  itemId,
  clearFetchLocationLoadingStateFunc,
}) {
  const { placeID } = location;
  try {
    const { results } = yield call(
      googleMapsApi.getGeocodeFromPlaceID,
      placeID,
    );

    const {
      address_components: addressComponent,
      formatted_address: formattedAddress,
      geometry,
      types,
    } = results[0];
    const formattedLocation = formatGeocode(
      formattedAddress,
      geometry,
      placeID,
      types,
      clearFetchLocationLoadingStateFunc,
    );
    formattedLocation.addressComponents =
      getAddressComponentFromGeoCode(addressComponent);
    if (action) {
      yield call(action, formattedLocation);
    }
    yield put(
      selectLocationSuccess(
        `${key}`,
        formattedLocation,
        geometry.location,
        itemId,
      ),
    );
  } catch (error) {
    yield put(throwError(error));
  }
}

export function* createAccessToken() {
  try {
    const { accessToken } = yield call(userApi.createAccessToken);
    yield put(createAccessTokenSuccess(accessToken.token));
  } catch (error) {
    yield put(createAccessTokenError());
  }
}

export function* loadOrganizations() {
  try {
    const options = {
      // Only fetch organizations that are either MCOs, Transportation Brokers or Healthcare Providers
      params: { industrySegments: allowedIndustrySegmentCodes },
    };
    const { organizations } = yield call(userApi.userOrganizations, options);
    yield put(loadOrganizationsSuccess(organizations));
  } catch (error) {
    yield put(loadOrganizationsError());
  }
}

export function* getUnreadNotifications() {
  const { user, activeOrganization } = yield select(makeSelectAppState());
  try {
    const options = {
      params: {
        limit: 1,
        page: 1,
        unreadBy: user.uuid,
        scopes: ['PATIENTS', 'TRIPS', 'GENERAL'],
        recipient: activeOrganization?.orgResourceId,
      },
    };
    const { notifications } = yield call(
      notificationApi.getNotifications,
      options,
    );
    const hasUnread = (notifications?.length ?? 0) > 0;
    yield put(getUnreadNotificationsSuccess(notifications, hasUnread));
  } catch (error) {
    yield put(getUnreadNotificationsError());
  }
}

/**
 * Error handling for the config reads PrivateContainer fires on mount of every
 * authenticated page.
 *
 * The user never asked for this data, so a 401/403 here describes their role,
 * not anything they just did -- there is no action for them to take and the
 * message will recur on every page load for as long as the role stands. Five of
 * these resolve at once, so the Home page greeted a user without Read Config
 * with a stack of identical "You don't have permission" toasts (TS-9513), and
 * Create Trip re-fired two of them mid-flow, making a trip the user was allowed
 * to book look like it had failed (TS-9463). Most of the sibling fetches in
 * that same batch already fail quietly; this makes the rest consistent.
 *
 * Transient failures (5xx, network) still toast -- those are worth reporting
 * because retrying may work.
 */
export function* reportBackgroundFetchError(error) {
  if (isAuthorizationError(error)) return;
  yield put(throwError(error));
}

export function* getLevelsOfService({ orgResourceId, params }) {
  try {
    const { page = 1 } = params || {};
    const { levelsOfService, totalAvailable } = yield call(
      configApi.getLOS,
      orgResourceId,
      {
        params: {
          ...params,
          sortBy: SORT_PARAMS.DISPLAY_TEXT,
          sortOrder: 1,
        },
      },
    );
    yield put(
      getLevelsOfServiceSuccess(levelsOfService, totalAvailable, page === 1),
    );
  } catch (error) {
    yield call(reportBackgroundFetchError, error);
  }
}

export function* getLocationModifiers({ orgResourceId, params }) {
  try {
    const { locationModifiers } = yield call(
      configApi.getLocationModifiers,
      orgResourceId,
      { params },
    );
    yield put(getLocationModifiersSuccess(locationModifiers));
  } catch (error) {
    yield call(reportBackgroundFetchError, error);
  }
}

export function* getAssignees({ orgResourceId }) {
  try {
    const { assignees } = yield call(contractApi.getAssignees, orgResourceId, {
      params: { sortBy: SORT_PARAMS.ASSIGNEE_NAME, sortOrder: 1 },
    });
    const { contracts } = yield call(contractApi.getContracts, orgResourceId);
    const tncContracts = {};
    contracts.forEach(({ assignee, uuid }) => {
      if (assignee.shortName) {
        if (assignee.shortName.toLowerCase() === TNC_PROVIDERS.UBER) {
          tncContracts[TNC_PROVIDERS.UBER] = uuid;
        }
        if (assignee.shortName.toLowerCase() === TNC_PROVIDERS.LYFT) {
          tncContracts[TNC_PROVIDERS.LYFT] = uuid;
        }
      }
    });
    yield all([
      put(setTncContracts(tncContracts)),
      put(fetchAssigneesSuccess(assignees)),
    ]);
  } catch (error) {
    yield all([
      put(fetchAssigneesError(error)),
      call(reportBackgroundFetchError, error),
    ]);
  }
}

export function* getContracts({ orgResourceId }) {
  try {
    const { contracts } = yield call(contractApi.getContracts, orgResourceId, {
      params: {
        sortBy: SORT_PARAMS.ASSIGNEE_NAME,
        sortOrder: 1,
        limit: 200,
      },
    });
    yield put(fetchContractsSuccess(contracts));
  } catch (error) {
    yield all([
      put(fetchContractsError(error)),
      put(throwErrorToastNotification(error)),
    ]);
  }
}

export function* getMemberPreferences({ orgResourceId }) {
  try {
    const { patientPreferences: memberPreferences } = yield call(
      configApi.getMemberPreferences,
      orgResourceId,
    );
    yield put(getMemberPreferencesSuccess(memberPreferences));
  } catch (error) {
    yield call(reportBackgroundFetchError, error);
    yield put(getMemberPreferencesFailed());
  }
}

export function* updateMemberPreferences({ orgResourceId, body }) {
  try {
    const { updatedPatientPreferences: updatedMemberPreferences } = yield call(
      configApi.updateMemberPreferences,
      orgResourceId,
      body,
    );
    yield put(throwSuccessMsg('Update Successful'));
    yield put(getMemberPreferencesSuccess(updatedMemberPreferences));
  } catch (error) {
    yield put(throwError(error));
    yield put(getMemberPreferencesFailed());
  }
}

export function* updateMember({ orgResourceId, memberUuid, updates }) {
  try {
    yield call(memberApi.updateMember, updates, memberUuid, orgResourceId);
    yield put(throwSuccessMsg('Update Successful'));
  } catch (error) {
    yield all([
      put(throwError('There was a problem updating member, please try again')),
    ]);
  }
}

export function* getAppointmentReasons({ orgResourceId }) {
  try {
    const options = {
      params: {
        sortBy: SORT_PARAMS.DISPLAY_TEXT,
        sortOrder: 1,
        limit: 1000,
        page: 1,
      },
    };
    const { appointmentReasons } = yield call(
      configApi.getAppointmentReasons,
      orgResourceId,
      options,
    );
    yield put(fetchAppointmentReasonsSuccess(appointmentReasons));
  } catch (error) {
    yield put(fetchAppointmentReasonsError());
  }
}

function* fetchLyftEstimationFromTAProxy(
  accessTokens,
  params,
  kinetikProviderId,
  payerId,
  requesterOrgResourceId,
) {
  let data;
  if (
    accessTokens.kinetik.token &&
    new Date().getTime() < accessTokens.kinetik.expiresAt
  ) {
    data = yield call(
      kinetikProxyApi.getTripEstimationLyft,
      {
        headers: {
          'X-Kinetik-Authorization': `Bearer ${accessTokens.kinetik.token}`,
        },
        params,
      },
      kinetikProviderId,
      payerId,
      requesterOrgResourceId,
    );
  } else {
    const response = yield call(kinetikProxyApi.getAccessToken);
    yield put(setKinetikProxyToken(response.accessToken, response.expiresIn));
    data = yield call(
      kinetikProxyApi.getTripEstimationLyft,
      {
        headers: {
          'X-Kinetik-Authorization': `Bearer ${response.accessToken}`,
        },
        params,
      },
      kinetikProviderId,
      payerId,
      requesterOrgResourceId,
    );
  }
  return data;
}

function* fetchLyftEstimationFromTSProxy(accessTokens, params) {
  let data;
  if (
    accessTokens.lyft.token &&
    new Date().getTime() < accessTokens.lyft.expiresAt
  ) {
    data = yield call(lyftApi.getTripEstimatation, {
      headers: {
        'X-Lyft-Authorization': `Bearer ${accessTokens.lyft.token}`,
      },
      params,
    });
  } else {
    const response = yield call(lyftApi.getAccessToken);
    yield put(setLyftToken(response.access_token, response.expires_in));
    data = yield call(lyftApi.getTripEstimatation, {
      headers: {
        'X-Lyft-Authorization': `Bearer ${response.access_token}`,
      },
      params,
    });
  }
  return data;
}

function* fetchLyftTripEstimation({ trip, assignee, requesterOrganization }) {
  const {
    request: { pickup, dropOff },
    uuid: tripUuid,
  } = trip;

  const params = {
    start_lat: pickup.longLat[1],
    start_lng: pickup.longLat[0],
    end_lat: dropOff.longLat[1],
    end_lng: dropOff.longLat[0],
  };

  const {
    industryIdentifiers: { kinetikProviderId },
  } = assignee;

  const { accessTokens } = yield select(makeSelectAppState());
  let data;

  try {
    if (kinetikProviderId)
      data = yield call(
        fetchLyftEstimationFromTAProxy,
        accessTokens,
        params,
        kinetikProviderId,
        requesterOrganization?.industryIdentifiers?.payerId,
        requesterOrganization.orgResourceId,
      );
    // this else block should be removed when new Compass proxy migration is complete
    // remember to remove all related constant, action, reducer
    else
      data = yield call(fetchLyftEstimationFromTSProxy, accessTokens, params);

    yield put(
      fetchEstimateSuccess(TNC_PROVIDERS.LYFT, data.cost_estimates, tripUuid),
    );
  } catch (error) {
    yield all([
      put(
        throwError({
          message: error.error ? error.error : error.response.data.message,
        }),
      ),
      put(fetchEstimateError()),
    ]);
  }
}

function* fetchUberTripEstimationFromTAProxy(
  accessTokens,
  body,
  kinetikProviderId,
  payerId,
  requesterOrgResourceId,
) {
  let data;
  if (
    accessTokens.kinetik.token &&
    new Date().getTime() < accessTokens.kinetik.expiresAt
  ) {
    data = yield call(
      kinetikProxyApi.getTripEstimationUber,
      {
        headers: {
          'X-Kinetik-Authorization': `Bearer ${accessTokens.kinetik.token}`,
        },
      },
      body,
      kinetikProviderId,
      payerId,
      requesterOrgResourceId,
    );
  } else {
    const response = yield call(kinetikProxyApi.getAccessToken);
    yield put(setKinetikProxyToken(response.accessToken, response.expiresIn));
    data = yield call(
      kinetikProxyApi.getTripEstimationUber,
      {
        headers: {
          'X-Kinetik-Authorization': `Bearer ${response.accessToken}`,
        },
      },
      body,
      kinetikProviderId,
      payerId,
      requesterOrgResourceId,
    );
  }
  return data;
}
function* fetchUberTripEstimation({ trip, assignee, requesterOrganization }) {
  const {
    request: { pickup, dropOff, bookingType, pickupDateTime },
    uuid: tripUuid,
  } = trip;
  let body = {
    pickup: {
      latitude: pickup.longLat[1],
      longitude: pickup.longLat[0],
    },
    dropoff: {
      latitude: dropOff.longLat[1],
      longitude: dropOff.longLat[0],
    },
  };

  switch (bookingType) {
    case BOOKING_TYPE_KEYS.SCHEDULED:
    case BOOKING_TYPE_KEYS.ARRIVE_BY:
      body = {
        ...body,
        scheduling: {
          pickup_time: DateTime.fromISO(pickupDateTime).toUTC().toMillis(),
        },
      };
      break;
    case BOOKING_TYPE_KEYS.WILLCALL:
      body = {
        ...body,
        deferred_ride_options: {
          pickup_day: DateTime.fromISO(pickupDateTime)
            .toUTC()
            .toFormat('yyyy-MM-dd'),
        },
      };
      break;
    case BOOKING_TYPE_KEYS.ONDEMAND:
    default:
      break;
  }

  const {
    industryIdentifiers: { kinetikProviderId },
  } = assignee;

  const { accessTokens } = yield select(makeSelectAppState());
  let data;
  try {
    if (kinetikProviderId)
      data = yield call(
        fetchUberTripEstimationFromTAProxy,
        accessTokens,
        body,
        kinetikProviderId,
        requesterOrganization?.industryIdentifiers?.payerId,
        requesterOrganization.orgResourceId,
      );
    else throw new Error('Provider setup incomplete');

    if (data.fares_unavailable)
      yield put(throwMiscError('Estimated fare is not available'));
    yield put(
      fetchEstimateSuccess(
        TNC_PROVIDERS.UBER,
        data.product_estimates,
        tripUuid,
      ),
    );
  } catch (error) {
    yield all([put(throwError(error)), put(fetchEstimateError())]);
  }
}

/**
 * Fetch the current user's role for the active org.
 *
 * A background refresh (`isRefresh`, e.g. on navigation) that fails keeps the
 * role already in state and reports quietly via reportBackgroundFetchError,
 * rather than toasting on every page change (TS-9877).
 *
 * @param {{isRefresh?: boolean}} [action] - the ON_LOAD_ROLES action
 * @returns {Generator}
 */
export function* getUserRoles({ isRefresh = false } = {}) {
  const orgResourceId = yield select(makeSelectActiveOrgResourceId());
  try {
    const { roles } = yield call(userApi.getRoles, orgResourceId);
    yield put(rolesLoaded(roles[0]));
  } catch (error) {
    if (isRefresh) {
      yield call(reportBackgroundFetchError, error);
    } else {
      yield put(throwError(error));
    }
  }
}

export function* getUnreadMessageCount() {
  try {
    const totalUnreadMessageCount = yield call(getTotalUnreadMessageCount);
    yield put(setUnreadMessageCount(totalUnreadMessageCount));
  } catch (error) {
    yield put(throwError(error));
  }
}

export function* getTripPreferences({ orgResourceId }) {
  try {
    const { tripPreferences } = yield call(
      configApi.getTripPreferences,
      orgResourceId,
    );
    yield put(getTripPreferencesSuccess(tripPreferences));
  } catch (error) {
    yield all([
      call(reportBackgroundFetchError, error),
      put(getTripPreferencesError()),
    ]);
  }
}

export function* updateTripPreferences({ orgResourceId, body }) {
  try {
    const { updatedTripPreferences } = yield call(
      configApi.updateTripPreferences,
      orgResourceId,
      body,
    );
    yield all([
      put(throwSuccessMsg('Update Successful')),
      put(updateTripPreferencesSuccess(updatedTripPreferences)),
    ]);
  } catch (error) {
    yield all([put(throwError(error)), put(updateTripPreferencesFailed())]);
  }
}

export function* getTripCancellationReasons({ orgResourceId }) {
  try {
    const options = {
      params: {
        sortBy: SORT_PARAMS.DISPLAY_TEXT,
        sortOrder: 1,
        limit: 1000,
        page: 1,
      },
    };
    const { tripCancellationReasons } = yield call(
      configApi.getTripCancellationReasons,
      orgResourceId,
      options,
    );
    yield put(getTripCancellationReasonsSuccess(tripCancellationReasons));
  } catch (error) {
    yield put(getTripCancellationReasonsError());
  }
}

export function* getTripDenialReasons({ orgResourceId }) {
  try {
    const options = {
      params: {
        sortBy: SORT_PARAMS.DISPLAY_TEXT,
        sortOrder: 1,
        limit: 1000,
        page: 1,
      },
    };
    const { tripDenialReasons } = yield call(
      configApi.getTripDenialReasons,
      orgResourceId,
      options,
    );
    yield put(getTripDenialReasonsSuccess(tripDenialReasons));
  } catch (error) {
    yield put(getTripDenialReasonsError());
  }
}

export function* getVehicleCategories({ orgResourceId }) {
  try {
    const { vehicleCategories } = yield call(
      configApi.getVehicleCategories,
      orgResourceId,
    );
    yield put(getVehicleCategoriesSuccess(vehicleCategories));
  } catch (error) {
    yield put(getVehicleCategoriesError(error));
  }
}

export function* getTripAssignmentPreferences({ orgResourceId }) {
  try {
    const { assignmentPreferences } = yield call(
      configApi.getAssignmentPreferences,
      orgResourceId,
    );
    yield put(getTripAssignmentPreferenceSuccess(assignmentPreferences));
  } catch (error) {
    yield all([
      call(reportBackgroundFetchError, error),
      put(getTripAssignmentPreferenceError()),
    ]);
  }
}

function* fetchLyftDriverAvailabilityFromTAProxy(
  accessTokens,
  params,
  kinetikProviderId,
  payerId,
  requesterOrgResourceId,
) {
  let data;
  if (
    accessTokens.kinetik.token &&
    new Date().getTime() < accessTokens.kinetik.expiresAt
  ) {
    data = yield call(
      kinetikProxyApi.getDriverAvailability,
      {
        headers: {
          'X-Kinetik-Authorization': `Bearer ${accessTokens.kinetik.token}`,
        },
        params,
      },
      kinetikProviderId,
      payerId,
      requesterOrgResourceId,
    );
  } else {
    const response = yield call(kinetikProxyApi.getAccessToken);
    yield put(setKinetikProxyToken(response.accessToken, response.expiresIn));
    data = yield call(
      kinetikProxyApi.getDriverAvailability,
      {
        headers: {
          'X-Kinetik-Authorization': `Bearer ${response.accessToken}`,
        },
        params,
      },
      kinetikProviderId,
      payerId,
      requesterOrgResourceId,
    );
  }
  return data;
}

function* fetchLyftDriverAvailabilityFromTSProxy(accessTokens, params) {
  let data;
  if (
    accessTokens.lyft.token &&
    new Date().getTime() < accessTokens.lyft.expiresAt
  ) {
    data = yield call(lyftApi.getDriverAvailability, {
      headers: {
        'X-Lyft-Authorization': `Bearer ${accessTokens.lyft.token}`,
      },
      params,
    });
  } else {
    const response = yield call(lyftApi.getAccessToken);
    yield put(setLyftToken(response.access_token, response.expires_in));
    data = yield call(lyftApi.getDriverAvailability, {
      headers: {
        'X-Lyft-Authorization': `Bearer ${response.access_token}`,
      },
      params,
    });
  }
  return data;
}

function* fetchLyftDriverAvailability({
  trip,
  assignee,
  requesterOrganization,
}) {
  const {
    request: { pickup, pickupDateTime, tripType },
    uuid: tripUuid,
  } = trip;

  const params = {
    origin_address: pickup?.addressText,
    origin_lat: pickup?.longLat[1],
    origin_lng: pickup?.longLat[0],
    pickup_time_ms: DateTime.fromISO(pickupDateTime).toMillis(),
    dispatch_type: 'scheduled',
  };

  const {
    industryIdentifiers: { kinetikProviderId },
  } = assignee;

  const { accessTokens } = yield select(makeSelectAppState());
  let data;
  try {
    if (
      tripType === BOOKING_TYPE_NAMES.ONDEMAND ||
      tripType === BOOKING_TYPE_NAMES.WILLCALL
    ) {
      throw new Error('Availability only supports scheduled trips');
    }

    if (kinetikProviderId) {
      data = yield call(
        fetchLyftDriverAvailabilityFromTAProxy,
        accessTokens,
        params,
        kinetikProviderId,
        requesterOrganization?.industryIdentifiers?.payerId,
        requesterOrganization.orgResourceId,
      );
    } else {
      data = yield call(
        fetchLyftDriverAvailabilityFromTSProxy,
        accessTokens,
        params,
      );
    }

    yield put(
      fetchLyftDriverAvailabilitySuccess(
        data?.driver_availability_predictions || [],
        tripUuid,
      ),
    );
  } catch (error) {
    yield all([
      // put(throwError({ message: error.error })),
      put(fetchLyftDriverAvailabilityError()),
    ]);
  }
}

function* getTripsCount({
  query,
  orgResourceId,
  sidebarMenuCountKey,
  countItineraries,
  groupBy,
}) {
  try {
    const options = {
      params: {
        ...query,
        fields: 'uuid',
        limit: 1,
        page: 1,
        ...(groupBy && { groupBy }),
      },
    };

    let apiFn;
    if (groupBy) {
      apiFn = tripApi.searchTripsGroupByView;
    } else if (countItineraries) {
      apiFn = tripApi.getItineraries;
    } else {
      apiFn = tripApi.getTrips;
    }

    const { totalAvailable } = yield call(apiFn, orgResourceId, options);

    yield put(setSidebarMenuCounts(sidebarMenuCountKey, totalAvailable));
  } catch (error) {
    // Gracefully handle error
  }
}

function* getNeedsActionTripsCount({
  orgResourceId,
  isCorrectionTableEnabled,
}) {
  try {
    const options = {
      params: {
        page: 1,
        limit: 1,
        groupBy: 'confirmationNumber',
        recurring: false,
        isRequest: false,
        status: TRIP_STATUS_CODES.APPROVAL_PENDING,
      },
    };

    const { totalAvailable: totalAvailableItineraries = 0 } = yield call(
      tripApi.searchTripsGroupByView,
      orgResourceId,
      options,
    );

    const { totalAvailable: totalAvailableRecurrings = 0 } = yield call(
      tripApi.searchRecurringItineraries,
      orgResourceId,
      {
        params: {
          page: 1,
          limit: 1,
          isRequest: false,
          status: TRIP_STATUS_CODES.APPROVAL_PENDING,
        },
      },
    );

    let totalAvailableCorrections = 0;
    if (isCorrectionTableEnabled) {
      const result = yield call(
        reconciliationApi.getReconciliations,
        orgResourceId,
        {
          params: {
            page: 1,
            limit: 1,
            isRequest: false,
            sessionStatus: 'UNDER_REVIEW',
          },
        },
      );
      totalAvailableCorrections = result.totalAvailable || 0;
    }

    yield put(
      setSidebarMenuCounts(
        'needsActionTrips',
        totalAvailableItineraries +
          totalAvailableRecurrings +
          totalAvailableCorrections,
      ),
    );
  } catch (error) {
    // Gracefully handle error
  }
}

function* getProCredExConnectionStatus({ orgResourceId }) {
  try {
    const options = {
      params: {
        isActive: true,
        classifications:
          'organization.verifyTransportationProviderNetworkCredentialing.viaProCredExHttpApi',
      },
    };
    const { jobRegistryEntries = [], totalAvailable = 0 } = yield call(
      configApi.getProCredExConnection,
      orgResourceId,
      options,
    );
    yield put(
      getProCredExConnectionStatusSuccess(jobRegistryEntries, totalAvailable),
    );
  } catch (error) {
    yield put(getProCredExConnectionStatusError());
  }
}

export function* getTripEscalationReasons({ orgResourceId }) {
  try {
    const options = {
      params: {
        sortBy: SORT_PARAMS.DISPLAY_TEXT,
        sortOrder: 1,
        limit: 1000,
        page: 1,
      },
    };
    const { tripApprovalRequestReasons } = yield call(
      configApi.getTripEscalationReasons,
      orgResourceId,
      options,
    );
    yield put(getTripEscalationReasonsSuccess(tripApprovalRequestReasons));
  } catch (error) {
    yield put(getTripEscalationReasonsError());
  }
}

export function* getTripApprovalReasons({ orgResourceId }) {
  try {
    const options = {
      params: {
        sortBy: SORT_PARAMS.DISPLAY_TEXT,
        sortOrder: 1,
        limit: 1000,
        page: 1,
      },
    };
    const { tripApprovalRequestApprovalReasons } = yield call(
      configApi.getTripApprovalReasons,
      orgResourceId,
      options,
    );
    yield put(
      getTripApprovalReasonsSuccess(tripApprovalRequestApprovalReasons),
    );
  } catch (error) {
    yield put(getTripApprovalReasonsError());
  }
}

function* getTripGroupings({ orgResourceId, params }) {
  try {
    const options = { params };

    const { tripGroupings } = yield call(
      configApi.getTripGroupings,
      orgResourceId,
      options,
    );

    yield put(getTripGroupingsSuccess(tripGroupings));
  } catch (error) {
    yield put(getTripGroupingsError());
  }
}

function* getMemberEnrollmentCoverageTripCounts({
  orgResourceId,
  tripGroupings,
  memberUuid,
  groupNumber,
  coverageProgramIdentificationNumber,
  requestedAppointmentReasonsClassification,
  coverageStartDate,
  coverageEndDate,
  years = [DateTime.now().year],
  medicarePlanCode,
  onSuccess,
  onError,
}) {
  try {
    const currentYear = DateTime.now().year;

    const enrollmentCoverageTripCounts = {};

    const tripCountsByYear = {};

    const targetTripGrouping = getTargetTripGrouping(
      tripGroupings,
      groupNumber,
      coverageProgramIdentificationNumber,
      requestedAppointmentReasonsClassification,
      medicarePlanCode,
    );

    // Construct a flat array of queries to be called in parallel;
    // should contain twice as many queries as there are years
    // (one query for completed trips in the year, and one query for scheduled trips in the year)
    const tripsQueriesForCompletedAndScheduledTripsForEveryYear = [];

    years.forEach((year) => {
      const restOfCurrentYearPeriod = [
        DateTime.now().toUTC().toISO(),
        DateTime.now().endOf('year').toUTC().toISO(),
      ];
      const yearPeriod = [
        DateTime.local(year).startOf('year').toUTC().toISO(),
        DateTime.local(year).endOf('year').toUTC().toISO(),
      ];

      const tripQuery = getTripQueryFromTripGrouping(
        targetTripGrouping,
        memberUuid,
        coverageProgramIdentificationNumber,
        yearPeriod,
      );

      // Construct a query for COMPLETED trips based on grouping-to-query translation
      const completedTripsQuery = {
        ...tripQuery,
        status: TRIP_STATUS_CODES.COMPLETED,
      };
      delete completedTripsQuery.statuses;

      // Construct a query for SCHEDULED trips based on grouping-to-query translation
      const scheduledTripsQueryRequestedPickupDateTimeRange =
        // If year in question is current year, search NOW until end of year,
        // otherwise, scope entire year
        year === currentYear ? restOfCurrentYearPeriod : yearPeriod;

      const scheduledTripsQuery = {
        ...tripQuery,
        statuses: tripQuery.statuses.filter(
          (status) => status !== TRIP_STATUS_CODES.COMPLETED,
        ),
        requestedPickupDateTimeRange:
          scheduledTripsQueryRequestedPickupDateTimeRange,
      };

      // Once COMPLETED and the cancelled statuses are removed a grouping can be left
      // with nothing to count (e.g. one configured as COMPLETED + CANCELLED only).
      // getTrips requires a non-empty `statuses`, so push null and resolve it as zero
      // instead of issuing a request that would 400. Null keeps the two-queries-per-year
      // positions that the result mapping below relies on.
      tripsQueriesForCompletedAndScheduledTripsForEveryYear.push(
        completedTripsQuery,
        scheduledTripsQuery.statuses.length ? scheduledTripsQuery : null,
      );
    });

    // Fetch completed and scheduled trip counts for all years in parallel
    const tripsResultsForEveryYear = yield all(
      tripsQueriesForCompletedAndScheduledTripsForEveryYear.map((params) =>
        params
          ? call(tripApi.getTrips, orgResourceId, { params })
          : Promise.resolve({ totalAvailable: 0 }),
      ),
    );

    /*
      tripsResultsForEveryYear looks similar to:
      [
        { totalAvailable: completedTripsCountYear0 }, { totalAvailable: scheduledTripsCountYear0 },
        { totalAvailable: completedTripsCountYear1 }, { totalAvailable: scheduledTripsCountYear1 },
        ...
      ]
    */

    // Map every 2 results to completed trips acount and scheduled trips count, respectively, for each year
    tripsResultsForEveryYear.forEach(({ totalAvailable: numTrips }, index) => {
      const yearIndex = Math.floor(index / 2);
      const year = years[yearIndex];

      // In queries, completed trips query was pushed first, then scheduled trips query
      // In results, numCompletedTrips has an even index, and numScheduledTrips has an odd index
      const tripCountTypeKey =
        index % 2 === 0 ? 'numCompletedTrips' : 'numScheduledTrips';

      // Initialize an empty object if trip count by year doesn't already exist
      if (!tripCountsByYear[year]) {
        tripCountsByYear[year] = {};
      }
      // Define number of completed|scheduled trips for the year
      tripCountsByYear[year][tripCountTypeKey] = numTrips;

      // Update number of total trips
      tripCountsByYear[year].numTotalTrips =
        (tripCountsByYear[year].numTotalTrips || 0) + numTrips;
    });

    enrollmentCoverageTripCounts[groupNumber] = {
      [`${coverageProgramIdentificationNumber}:${coverageStartDate}:${coverageEndDate}`]:
        {
          tripCountsByYear,
          customContextualAttributes:
            targetTripGrouping.customContextualAttributes,
          coveredTripClassifications:
            targetTripGrouping?.staticGroupByAttributes
              ?.requestedAppointmentReasonsClassifications,
        },
    };

    yield put(
      getMemberEnrollmentCoverageTripCountsSuccess(
        memberUuid,
        enrollmentCoverageTripCounts,
      ),
    );

    if (onSuccess) {
      yield call(onSuccess, enrollmentCoverageTripCounts);
    }
  } catch (error) {
    yield put(getMemberEnrollmentCoverageTripCountsError());

    if (onError) {
      yield call(onError, error);
    }
  }
}

function* globalSearch({ orgResourceId, searchValue, includeRequests }) {
  const { activeOrganization } = yield select(makeSelectAppState());
  try {
    const baseSearchParams = {
      searchValue,
      limit: GLOBAL_SEARCH_RESULTS_LIMIT_PER_RESOURCE,
      isGlobalSearch: true,
      searchValueTimezone:
        activeOrganization?.timezone || DEFAULT_SEARCH_VALUE_TIMEZONE,
    };

    const searchResults = yield all([
      call(memberApi.searchMembers, orgResourceId, {
        params: baseSearchParams,
      }),
      call(tripApi.searchTrips, orgResourceId, {
        params: { ...baseSearchParams, isRequest: false },
      }),
      includeRequests
        ? call(tripApi.searchTrips, orgResourceId, {
            params: { ...baseSearchParams, isRequest: true },
          })
        : {},
      call(contractApi.searchContracts, orgResourceId, {
        params: baseSearchParams,
      }),
    ]);

    const [
      { patients: members = [], totalAvailable: totalMembersAvailable = 0 },
      { trips = [], totalAvailable: totalTripsAvailable = 0 },
      { trips: requests = [], totalAvailable: totalRequestsAvailable = 0 },
      {
        contracts: providers = [],
        totalAvailable: totalProvidersAvailable = 0,
      },
    ] = searchResults;

    yield put(
      globalSearchSuccess({
        memberResults: {
          data: members,
          totalAvailable: totalMembersAvailable,
        },
        tripResults: {
          data: trips,
          totalAvailable: totalTripsAvailable,
        },
        requestResults: {
          data: requests,
          totalAvailable: totalRequestsAvailable,
        },
        providerResults: {
          data: providers,
          totalAvailable: totalProvidersAvailable,
        },
      }),
    );
  } catch (error) {
    yield all([put(throwError(error)), put(globalSearchError(error))]);
  }
}

export default function* appSaga() {
  yield all([
    customDebounce(250, SEARCH_MEMBERS, searchMembers),
    takeLatest(ON_LOAD_USER, loadingUser),
    takeLatest(ON_LOGOUT, logout),
    debounce(250, ON_FETCH_LOCATIONS, fetchLocations),
    debounce(250, ON_SELECT_LOCATION, fetchGeocode),
    takeLatest(CREATE_ACCESS_TOKEN, createAccessToken),
    takeLatest(ON_LOAD_ORGANIZATIONS, loadOrganizations),
    takeLatest(GET_UNREAD_NOTIFICATIONS, getUnreadNotifications),
    takeLatest(ON_GET_LEVELS_OF_SERVICE, getLevelsOfService),
    takeLatest(ON_GET_LOCATION_MODIFIERS, getLocationModifiers),
    takeLatest(ON_FETCH_ASSIGNEES, getAssignees), // replace it with new api
    takeLatest(ON_FETCH_CONTRACTS, getContracts),
    takeLatest(ATTEMPT_GET_MEMBER_PREFERENCES, getMemberPreferences),
    takeLatest(ATTEMPT_UPDATE_MEMBER, updateMember),
    takeLatest(ATTEMPT_UPDATE_MEMBER_PREFERENCES, updateMemberPreferences),
    takeLatest(ON_FETCH_APPOINTMENT_REASONS, getAppointmentReasons),
    takeEvery(GET_LYFT_TRIP_ESTIMATION, fetchLyftTripEstimation),
    takeEvery(GET_UBER_TRIP_ESTIMATION, fetchUberTripEstimation),
    takeLatest(ON_LOAD_ROLES, getUserRoles),
    takeLatest(GET_UNREAD_MESSAGE_COUNT, getUnreadMessageCount),
    takeLatest(GET_TRIP_PREFERENCES, getTripPreferences),
    takeLatest(ATTEMPT_UPDATE_TRIP_PREFERENCES, updateTripPreferences),
    takeLatest(GET_TRIP_CANCELLATION_REASONS, getTripCancellationReasons),
    takeLatest(GET_TRIP_DENIAL_REASONS, getTripDenialReasons),
    takeLatest(GET_VEHICLE_CATEGORIES, getVehicleCategories),
    takeLatest(GET_TRIP_ASSIGNMENT_PREFERENCE, getTripAssignmentPreferences),
    takeEvery(FETCH_LYFT_DRIVER_AVAILABILITY, fetchLyftDriverAvailability),
    takeEvery(GET_TRIPS_COUNT, getTripsCount),
    takeEvery(GET_NEEDS_ACTION_TRIPS_COUNT, getNeedsActionTripsCount),
    takeLatest(GET_PROCREDEX_CONNECTION_STATUS, getProCredExConnectionStatus),
    takeLatest(GET_TRIP_ESCALATION_REASON, getTripEscalationReasons),
    takeLatest(GET_TRIP_APPROVAL_REASON, getTripApprovalReasons),
    takeLatest(GET_TRIP_GROUPINGS, getTripGroupings),
    takeEvery(
      GET_MEMBER_ENROLLMENT_COVERAGE_TRIP_COUNTS,
      getMemberEnrollmentCoverageTripCounts,
    ),
    debounce(250, GLOBAL_SEARCH, globalSearch),
  ]);
}
